import React from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import Image from "../../components/image"
import Video from "../../components/video"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import BarChart from "../../components/bar-chart"
import StaticChart from "../../components/static-chart"
import MobileOnly from "../../components/mobile-only"
import { StatContainer, Stat } from "../../components/stat"
import ChartFinancialSupport from "../../images/spotlights/covid-19/financial-support.inline.svg"
import ReadNext from "../../components/read-next"
import Table from "../../components/table"
import GovernmentRevenueData from "../../data/tables/government-revenue.json"
import FiscalResponseData from "../../data/tables/covid-19-response.json"
import MotionIcon from "../../components/motion-icon"
import IconHandGlobe from "../../images/spotlights/covid-19/icon-hand-globe.inline.svg"

const PAGESLUG = "covid-19"

const video = (<Video
  videoNative={
    withPrefix("/") + "video/covid-19/covid.mp4"
  }
  track={
    withPrefix("/") + "video/covid-19/covid.vtt"
  }
  posterImage={"spotlights/covid-19/video-poster.jpg"}
/>)

export default function Covid19() {
  return (
    <Layout>
      <section className="page page--margin-large page--pullBottom">
        <Hero
          title="COVID-19"
          text="A second Great Depression was averted, thanks to an unprecedented global policy response."
          image="spotlights/covid-19/hero.jpg"
          video={withPrefix("/") + "video/covid-19/hero.mp4?v=1"}
        />

        <PageColumns>
          <Column type="media">
            <BarChart
              id="government-revenue-and-spending-measures"
              figureNumber="1.1"
              title="Government Spending Prevented Another Great Depression"
              subTitle="(percent of 2020 GDP)"
              caption="Extraordinary policy measures helped put a floor under the global economy."
              source="Sources: IMF, Fiscal Monitor Database of Country Fiscal Measures in Response to the COVID-19 Pandemic; and IMF staff calculations. <br/> Note: AEs = advanced economies; EMs = emerging markets; LIDCs = low-income developing countries."
            >
              <Table
                data={GovernmentRevenueData}
                figureNumber="1.1"
                title="Government Spending Prevented Another Great Depression"
                subTitle="(percent of 2020 GDP)"
                caption="Extraordinary policy measures helped put a floor under the global economy."
                source="Sources: IMF, Fiscal Monitor Database of Country Fiscal Measures in Response to the COVID-19 Pandemic; and IMF staff calculations. <br/> Note: AEs = advanced economies; EMs = emerging markets; LIDCs = low-income developing countries."
              />
            </BarChart>
            <StatContainer>
              <MotionIcon icon="hand-globe">
                <IconHandGlobe />
              </MotionIcon>
              <Stat
                prefix="$"
                number={110}
                suffix="+"
                note="billion in loans to"
              />
              <Stat
                number={86}
                note="countries since the onset of the crisis"
              />
            </StatContainer>
          </Column>

          <Column type="text">
            <p className="dropcap">
              A year into the global COVID-19 pandemic, a recovery is finally
              underway. After a severe contraction in 2020, global activity is
              on a firmer footing as economies continue to adapt to new ways of
              working and as vaccines are administered. Swift policy action
              worldwide, including extraordinary monetary measures and $16
              trillion in fiscal support, helped prevent another Great
              Depression (see Figure 1.1). Without these measures, the global
              contraction last year would have been three times worse.
            </p>
            <p>
              The IMF acted swiftly to provide financial assistance to an
              unprecedented number of countries facing urgent balance of
              payments needs in the wake of the pandemic. To meet demand, access
              limits for emergency financing and the IMF’s lending facilities
              were temporarily increased through the end of 2021. In total,
              loans to 86 countries of more than $110 billion have been approved
              since the onset of the crisis, bringing the IMF’s total lending
              commitments to more than $110 billion, with more than one-third
              approved since late March 2020 (see Figure 1.2).
            </p>
            <MobileOnly>
              <StatContainer>
                <MotionIcon icon="hand-globe">
                  <IconHandGlobe />
                </MotionIcon>
                <Stat
                  prefix="$"
                  number={110}
                  suffix="+"
                  note="billion in loans to"
                />
                <Stat
                  number={86}
                  note="countries since the onset of the crisis"
                />
              </StatContainer>
            </MobileOnly>
            <p>
              To further support the recovery, a proposal for a general
              allocation of special drawing rights of $650 billion (about SDR
              453 billion) has been circulated to the IMF’s Executive Board for
              consideration. The allocation will be the largest in IMF history,
              providing a substantial liquidity boost to countries, without
              adding to debt burdens, by supplementing existing reserve assets.
              This will free resources for much-needed health and recovery
              efforts. The IMF is also working on options to channel SDR
              holdings voluntarily to help vulnerable countries recover.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={8} />
            <MobileOnly>
              <BarChart
                id="government-revenue-and-spending-measures"
                figureNumber="1.1"
                title="Government Spending Prevented Another Great Depression"
                subTitle="(percent of 2020 GDP)"
                caption="Extraordinary policy measures helped put a floor under the global economy."
                source="Sources: IMF, Fiscal Monitor Database of Country Fiscal Measures in Response to the COVID-19 Pandemic; and IMF staff calculations. <br/> Note: AEs = advanced economies; EMs = emerging markets; LIDCs = low-income developing countries."
              >
                <Table
                  data={GovernmentRevenueData}
                  figureNumber="1.1"
                  title="Government Spending Prevented Another Great Depression"
                  subTitle="(percent of 2020 GDP)"
                  caption="Extraordinary policy measures helped put a floor under the global economy."
                  source="Sources: IMF, Fiscal Monitor Database of Country Fiscal Measures in Response to the COVID-19 Pandemic; and IMF staff calculations. <br/> Note: AEs = advanced economies; EMs = emerging markets; LIDCs = low-income developing countries."
                />
              </BarChart>
            </MobileOnly>
            <p>
              To provide up-to-date, ongoing information on the policies
              governments implemented to limit the impact of the COVID-19
              pandemic, the IMF has created a policy tracker that summarizes
              governments’ key economic responses to the pandemic. The tracker
              includes data for 197 economies and is updated regularly.{" "}
            </p>
          </Column>
        </PageColumns>

        <PageColumns>
          <div
            className="container md:container--large"
            style={{ marginTop: 20, marginBottom: 20 }}
          >
            <StaticChart
              figureNumber="1.2"
              title="IMF Financial Support"
              subTitle="(by year of approval; billions of SDRs)"
              caption="The IMF received an unprecedented number of requests for emergency financing."
              source="Sources: IMF, Monitoring of Fund Arrangements database; IMF Finance Department; and IMF, Strategy, Policy, and Review Department. <br/> Note: Data for 2021 are for January–April only and do not cover a full calendar year. ECF = Extended Credit Facility; EFF = Extended Fund Facility; FCL = Flexible Credit Line; PLL = Precautionary and Liquidity Line; RCF = Rapid Credit Facility; RFI = Rapid Financing Instrument; SBA = Stand-By Arrangement; SCF = Standby Credit Facility. <br/> ¹PLL/FCL and RFI/RCF figures include predecessor instruments."
              ariaLabel="Bar chart showing IMF Financial Support from 2002 to 2020"
              classes="chart--financial-support"
              scaling={false}
              paddingbottom="100%"
            >
              <ChartFinancialSupport />
            </StaticChart>
          </div>
        </PageColumns>

        {/* Debt Relief */}
        <PageColumns>
          <Column type="text">
            <h2>Debt relief</h2>
            <p>
              Low-income countries have been hit by a confluence of external
              shocks: a sharp contraction in real exports, lower export prices,
              and reduced remittances and tourism receipts. They entered the
              crisis with limited resources and high debt, significantly
              constraining their capacity to respond (see Figure 1.3).
            </p>
            <p>
              Support from the international community is critical for enabling
              these countries to recover from the pandemic. This includes
              support for debt relief, which the IMF has provided to 29 of its
              poorest members through its Catastrophe Containment and Relief
              Trust (CCRT). Additional resources are being sought to provide
              debt relief for a full two-year period through April 2022, to
              cover the debt relief from October 2021 to April 2022 and to
              ensure that adequate grant resources are in place to address the
              needs of members under other CCRT-qualifying shocks in the future.
              Thus far, contributions totaling $785 million have come from
              donors including Bulgaria, China, the European Union, France,
              Germany, Japan, Luxembourg, Malta, Mexico, Netherlands, Norway,
              the Philippines, Singapore, Sweden, Switzerland, and the United
              Kingdom.
            </p>
            <MobileOnly>
              <BarChart
                id="selected-economic-indicators"
                figureNumber="1.3"
                title="Countries’ Ability to Scale Up Fiscal Support Has Varied"
                subTitle="(fiscal response to COVID-19 in 2020)"
                caption="Crisis response in low-income countries was constrained by limited financial resources."
                source="Sources: IMF, Fiscal Monitor and World Economic Outlook databases; and IMF staff calculations. <br/> Note: LICs = low-income countries; PRGT = Poverty Reduction and Growth Trust. ¹ Including the provision of equity, loans, and guarantees."
              >
                <Table
                  data={FiscalResponseData}
                  figureNumber="1.3"
                  title="Countries’ Ability to Scale Up Fiscal Support Has Varied"
                  subTitle="(fiscal response to COVID-19 in 2020)"
                  caption="Crisis response in low-income countries was constrained by limited financial resources."
                  source="Sources: IMF, Fiscal Monitor and World Economic Outlook databases; and IMF staff calculations. <br/> Note: LICs = low-income countries; PRGT = Poverty Reduction and Growth Trust. ¹ Including the provision of equity, loans, and guarantees."
                />
              </BarChart>
            </MobileOnly>
            <AppendixWaypoint page={PAGESLUG} id={5} />
            <p>
              Debt relief has also been made available through the Group of
              Twenty (G20) Debt Service Suspension Initiative (DSSI), which the
              IMF, together with the World Bank, has helped support. After
              taking effect in May 2020, the initiative delivered $5.7 billion
              in debt relief to 43 countries by the end of 2020. Two six-month
              extensions of the initiative until the end of 2021 will provide
              considerable debt service relief.
            </p>
          </Column>

          <Column type="media">
            <BarChart
              id="selected-economic-indicators"
              figureNumber="1.3"
              title="Countries’ Ability to Scale Up Fiscal Support Has Varied"
              subTitle="(fiscal response to COVID-19 in 2020)"
              caption="Crisis response in low-income countries was constrained by limited financial resources."
              source="Sources: IMF, Fiscal Monitor and World Economic Outlook databases; and IMF staff calculations. <br/> Note: LICs = low-income countries; PRGT = Poverty Reduction and Growth Trust. <br/> ¹Including the provision of equity, loans, and guarantees."
            >
              <Table
                data={FiscalResponseData}
                classes="table--col1-large"
                figureNumber="1.3"
                title="Countries’ Ability to Scale Up Fiscal Support Has Varied"
                subTitle="(fiscal response to COVID-19 in 2020)"
                caption="Crisis response in low-income countries was constrained by limited financial resources."
                source="Sources: IMF, Fiscal Monitor and World Economic Outlook databases; and IMF staff calculations. <br/> Note: LICs = low-income countries; PRGT = Poverty Reduction and Growth Trust. <br/> ¹ Including the provision of equity, loans, and guarantees."
              />
            </BarChart>
          </Column>
        </PageColumns>

        {/* Promoting Good Governance */}
        <PageColumns modifiers="md:pull--medium">
          <Column type="media">
            {video}
          </Column>

          <Column type="text">
            <h2>Promoting good governance</h2>
            <p>
              Governments around the world are playing a bigger role in their
              economies, expediting spending and delivery of services to combat
              the pandemic. This expanded role is crucial, but it may also
              increase opportunities for corruption. Recognizing these risks,
              the IMF has called for enhanced transparency and accountability
              measures. Its advice is clear: “Do what it takes, but keep the
              receipts.”
            </p>
            <MobileOnly>
              {video}
            </MobileOnly>
            <p>
              The IMF has sought specific governance measures for countries
              receiving emergency financing. These measures include commitments
              to publish COVID-19 spending reports and audit results, as well as
              crisis-related procurement contracts, including the companies
              awarded the contracts and their benefiting owners. This
              information is key to preventing conflicts of interest and tax
              evasion and allowing the public to track who benefits from public
              contracts. In addition, countries receiving assistance commit to
              undertaking a Safeguards Assessment: a diagnostic review of a
              central bank’s governance and control framework.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={6} />
            <p>
              Longer-term governance and corruption vulnerabilities continue to
              be addressed under the IMF’s broader 2018 Framework for Enhanced
              Fund Engagement on Governance, with a focus on multiyear IMF
              lending arrangements and surveillance, including in the context of
              voluntary assessments of transnational aspects of corruption. The
              IMF is also stepping up its technical assistance and training in
              areas such as public financial management, anticorruption and
              anti–money laundering frameworks, fiscal transparency, and tax
              administration, including through deep-dive governance diagnostic
              missions. Thus far, more than a dozen of these missions have been
              completed or are underway. Collaboration with other international
              institutions, including the World Bank, the G20 Anti-Corruption
              Working Group, the Organisation for Economic Co-operation and
              Development Working Group on Bribery in International Business
              Transactions, and the Financial Action Task Force, has also ramped
              up, as well as cooperation with civil society organizations.
            </p>
          </Column>
        </PageColumns>

        {/* Going Virtual */}
        <PageColumns modifiers="md:pull--large">
          <Column type="text">
            <h2>Going virtual</h2>
            <p>
              Like other organizations, the IMF had to quickly adjust to new
              ways of working as a result of the pandemic. Board meetings,
              surveillance missions, program reviews, and CD all went virtual.
            </p>
            <p>
              Immediate and real-time policy advice and CD were provided
              virtually to 160 countries during FY 2020/2021 on topics ranging
              from debt management to governance. Despite connectivity
              limitations, most of these virtual engagements were with fragile
              and conflict-affected states, low-income countries, and small
              states. A new Regional Capacity Development Center opened in
              February 2021 for the Caucasus, Central Asia, and Mongolia. Online
              learning, webinars, and training curricula were also expanded
              during the year.
            </p>
            <MobileOnly>
              <Image
                path="spotlights/covid-19/going-virtual.jpg"
                alt="Lagos, Nigeria. Wole Olayinka, a 27 years old communications professional has been working from home since the break of the pandemic in Lagos."
              />
            </MobileOnly>
            <p>
              Both the 2020 Annual and 2021 Spring Meetings were held online,
              with thousands of delegates participating online from countries
              all over the world in hundreds of events. The success of these
              meetings is indicative of the remarkable adaptability the IMF
              community has shown.
            </p>
          </Column>

          <Column type="media">
            <Image
              path="spotlights/covid-19/going-virtual.jpg"
              alt="Lagos, Nigeria. Wole Olayinka, a 27 years old communications professional has been working from home since the break of the pandemic in Lagos."
            />
          </Column>
        </PageColumns>
      </section>

      <ReadNext sectionID="spotlight" pageID="the-great-divergence" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
